import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '..';
import config from '../../config';

export interface Shop {
  _id: string;
  name: string;
  address: string;
  contact_number: string;
  owner_id?: string;
  category_id?: string;
  category?: {
    _id: string;
    name: string;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateShopRequest {
  name: string;
  address: string;
  contact_number: string;
  category_id: string;
  owner_id?: string;
}

export interface UpdateShopRequest {
  name?: string;
  address?: string;
  contact_number?: string;
  category_id?: string;
  owner_id?: string;
  is_active?: boolean;
}

export const shopsApi = createApi({
  reducerPath: 'shopsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: config.api.baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Shops'],
  endpoints: (builder) => ({
    getShops: builder.query<Shop[], void>({
      query: () => '/shops',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Shops' as const, id: _id })),
              { type: 'Shops', id: 'LIST' },
            ]
          : [{ type: 'Shops', id: 'LIST' }],
    }),
    getShop: builder.query<Shop, string>({
      query: (id) => `/shops/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Shops', id }],
    }),
    createShop: builder.mutation<Shop, CreateShopRequest>({
      query: (shopData) => ({
        url: '/shops',
        method: 'POST',
        body: shopData,
      }),
      invalidatesTags: [{ type: 'Shops', id: 'LIST' }],
    }),
    updateShop: builder.mutation<Shop, { id: string; data: UpdateShopRequest }>({
      query: ({ id, data }) => ({
        url: `/shops/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Shops', id },
        { type: 'Shops', id: 'LIST' },
      ],
    }),
    deleteShop: builder.mutation<void, string>({
      query: (id) => ({
        url: `/shops/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Shops', id },
        { type: 'Shops', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetShopsQuery,
  useGetShopQuery,
  useCreateShopMutation,
  useUpdateShopMutation,
  useDeleteShopMutation,
} = shopsApi; 